import { hashString } from "@/lib/hash";

import { TIME } from "../policy";
import type { ShareItem, ShareRights } from "../types";

/**
 * Fixed demo tokens, so the landing page, the end-to-end tests and anyone clicking around share the same links.
 * Each is 128 random bits in base64url, like the tokens the mock mints at runtime. The mock still stores and
 * compares them only as SHA-256 hashes (FR-ADMIN-032).
 */
export const DEMO_TOKENS = {
  memberInvitation: "t9jJmvMWiYmtxj7bI-QdWQ",
  guestInvitation: "pCwk-Mh5uVqdT5Xeyo3XFg",
  expiredInvitation: "aNV6u7O0Vwvn8tPSJ52UoQ",
  revokedInvitation: "sBiDdmMPPeY6aWZ0ZSLwFw",
  share: "y6jXRma5oooJVqiXpNv4EQ",
  sharePassword: "ltV2LMHSXTGIhd0Oz1AhuA",
  shareExpired: "YMFkPBSP5_6erOtaiUyV4Q",
  shareRevoked: "Opw704zlgCm66CACQsJHsQ",
} as const;

/** Password of the protected demo share link (8+ characters, FR-COLLAB-103). Shown on the landing page. */
export const DEMO_SHARE_PASSWORD = "monsoon-2026";

export interface DemoShareLink {
  linkId: string;
  token: string;
  title: string;
  message: string | undefined;
  projectName: string;
  senderName: string;
  items: ShareItem[];
  rights: ShareRights;
  /** Days from now; negative means it has expired. */
  expiresInDays: number;
  revoked: boolean;
  password: string | undefined;
}

function item(
  projectId: string,
  name: string,
  format: ShareItem["format"],
  kind: ShareItem["kind"],
  scale: string,
  number?: string,
): ShareItem {
  return {
    // Unique per link: a drawing and the sheet that lays it out share a name.
    id: `${projectId}:${format}:${number === undefined ? "" : `${number}:`}${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
    name,
    format,
    kind,
    number,
    scale,
    seed: hashString(`${projectId}:${name}`),
  };
}

export const DEMO_SHARE_LINKS: readonly DemoShareLink[] = [
  {
    linkId: "sl-bodakdev-options",
    token: DEMO_TOKENS.share,
    title: "Plan options for review",
    message:
      "Here are the plan options we discussed on Thursday. Comment on anything you would like changed.",
    projectName: "Bodakdev Residence",
    senderName: "Asha Rao",
    items: [
      item("bodakdev", "Ground floor plan", "drawing", "plan", "1:100"),
      item("bodakdev", "First floor plan", "drawing", "plan", "1:100"),
      item("bodakdev", "Site plan", "drawing", "site", "1:200"),
      item("bodakdev", "Ground floor plan", "sheet", "plan", "1:100", "A-101"),
      item("bodakdev", "Front elevation", "sheet", "elevation", "1:100", "A-201"),
    ],
    rights: { view: true, comment: true, download: false },
    expiresInDays: 24,
    revoked: false,
    password: undefined,
  },
  {
    linkId: "sl-kochi-concept",
    token: DEMO_TOKENS.sharePassword,
    title: "Concept set",
    message:
      "Concept drawings for the courtyard scheme. PDFs can be downloaded for your board meeting.",
    projectName: "Kochi Boutique Hotel",
    senderName: "Priya Nair",
    items: [
      item("kochi", "Site plan", "drawing", "site", "1:200"),
      item("kochi", "Ground floor plan", "sheet", "plan", "1:100", "A-101"),
      item("kochi", "Section A-A", "sheet", "section", "1:100", "A-301"),
    ],
    rights: { view: true, comment: false, download: true },
    expiresInDays: 10,
    revoked: false,
    password: DEMO_SHARE_PASSWORD,
  },
  {
    linkId: "sl-whitefield-layouts",
    token: DEMO_TOKENS.shareExpired,
    title: "Layout options",
    message: undefined,
    projectName: "Whitefield Township",
    senderName: "Neha Kulkarni",
    items: [item("whitefield", "Layout plan", "drawing", "layout", "1:1000")],
    rights: { view: true, comment: true, download: false },
    expiresInDays: -3,
    revoked: false,
    password: undefined,
  },
  {
    linkId: "sl-bandra-kitchen",
    token: DEMO_TOKENS.shareRevoked,
    title: "Kitchen drawings",
    message: undefined,
    projectName: "Bandra Sea-view Apartment",
    senderName: "Meera Iyer",
    items: [item("bandra", "Kitchen elevations", "sheet", "elevation", "1:20", "I-401")],
    rights: { view: true, comment: false, download: false },
    expiresInDays: 12,
    revoked: true,
    password: undefined,
  },
];

export function shareExpiry(link: DemoShareLink, now: number): number {
  return now + link.expiresInDays * TIME.MS_PER_DAY;
}
